import { DeleteOutlined } from '@ant-design/icons';
import { useFormValues } from '@framework/form/form/useFormValues.ts';
import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { $LabeledSpaceCompact } from '@framework/ui/components/space/SpaceCompact.tsx';
import { Button } from 'antd';
import { useCallback } from 'react';
import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import { type IWidgetOptionValue } from '../../../../Widget.ts';
import {
  type AdvancedSelectColumnDatatype,
  AdvancedSelectColumnDatatypes,
} from '../IAdvancedSelectColumns.ts';
import { convertColumnValue } from '../utils/convertColumnValue.ts';

const dataTypeLabels: Record<AdvancedSelectColumnDatatype, string> = {
  [Datatype.String]: 'Testo',
  [Datatype.Number]: 'Numero',
  [Datatype.Boolean]: 'Booleano',
  [Datatype.Date]: 'Data',
  [Datatype.RichText]: 'Testo formattato',
};

const dataTypeOptions = AdvancedSelectColumnDatatypes.map(value => ({
  label: dataTypeLabels[value],
  value,
}));

export interface IAdvancedSelectColumnConfigProps {
  /** Posizione della colonna nell'array `columns` del form. */
  index: number;
  /** Callback per rimuovere la colonna. */
  onDelete: () => void;
}
/**
 * Configurazione di una singola colonna della tabella AdvancedSelect
 */
export function AdvancedSelectColumnConfig(
  props: IAdvancedSelectColumnConfigProps,
) {
  const { index, onDelete } = props;
  const setValue = useFormSetValue();
  const formValues = useFormValues();

  const column = formValues.columns?.[index];
  const columnId = column?.id;
  const previousType = column?.type;

  // Riconverte i valori delle opzioni al cambio di tipo della colonna.
  // Eseguito nell'handler del select (non in un effect) così la conversione è
  // atomica col cambio tipo, senza render intermedio né tipo precedente in ref.
  const convertOptionsOnTypeChange = useCallback(
    (newType: AdvancedSelectColumnDatatype) => {
      if (
        previousType == null ||
        previousType === newType ||
        columnId == null
      ) {
        return;
      }

      // Una colonna RichText non può fare da valore univoco: se la colonna
      // appena convertita era quella selezionata, la selezione viene azzerata
      // per non lasciare un id orfano fuori dalle opzioni del Select.
      if (
        newType === Datatype.RichText &&
        formValues.uniqueValueColumnId === columnId
      ) {
        setValue('uniqueValueColumnId', null);
      }

      const options = formValues.options as IWidgetOptionValue[] | undefined;
      if (options == null || options.length === 0) {
        return;
      }

      const updatedOptions = options.map(option => {
        if (option.values?.[columnId] == null) {
          return option;
        }

        return {
          ...option,
          values: {
            ...option.values,
            [columnId]: convertColumnValue(
              option.values[columnId],
              previousType,
              newType,
            ),
          },
        };
      });

      setValue('options', updatedOptions);
    },
    [previousType, columnId, formValues, setValue],
  );

  return (
    <$LabeledSpaceCompact block size="large" key={index}>
      <FormItem name={`columns.${index}.id`} label={'Codice'}>
        <TextInput name={`columns.${index}.id`} />
      </FormItem>
      <FormItem name={`columns.${index}.label`} label={'Etichetta'}>
        <TextInput name={`columns.${index}.label`} />
      </FormItem>
      <FormItem name={`columns.${index}.type`} label={'Tipo'}>
        <SelectInput
          name={`columns.${index}.type`}
          options={dataTypeOptions}
          onChange={(newType, _option, onChange) => {
            onChange(newType);
            convertOptionsOnTypeChange(newType as AdvancedSelectColumnDatatype);
          }}
        />
      </FormItem>
      <Button
        onClick={onDelete}
        danger
        icon={<DeleteOutlined aria-label="Rimuovi colonna" />}
      />
    </$LabeledSpaceCompact>
  );
}
